/**
 * Greedy chunk mesher with per-vertex ambient occlusion and smooth lighting.
 *
 * Runs inside a mesh worker on a padded neighbourhood (see world/padded.ts):
 * the chunk plus a one-voxel border, so faces, AO and light are seamless
 * across chunk borders and every lookup is a single typed-array read.
 *
 * Greedy meshing: coplanar faces with identical block, tile, orientation and
 * identical per-corner light/AO are merged into large quads. UVs run in
 * tile-units (0..w, 0..h) and the shader repeats the tile via fract() into a
 * gutter-padded atlas, so merged quads tile correctly.
 *
 * Output is a packed 20-byte vertex in chunk-local space (see VERTEX FORMAT),
 * four vertices per quad in an order that makes the fixed index pattern
 * (0,1,2, 0,2,3) produce the AO-correct diagonal and winding.
 */

import { CHUNK_SIZE, WORLD_HEIGHT } from '../constants';
import {
  AIR, BLOCKS, FACE_DIRS, KELP_ID, T, FLAT_ALIGNED, FLAT_Y, RAIL_SHAPE, GLASS_ID, IS_OPAQUE, IS_RIGID, IS_SOLID, IS_TORCH, IS_WATERY, LIGHT_EMISSION, LIGHT_FILTER, Pass,
  RENDER_PASS, RENDER_SHAPE, RenderShape, SURFACE_DROP, TILE_COUNT, TINTABLE_TILES, tintOf, TORCH_FACING, WATER_AMOUNT, WATER_ID,
  NETHER_PORTAL_ID, NETHER_PORTAL_Z_ID, IS_LAVA, LAVA_AMOUNT, LAVA_ID, IS_CROP_MODEL, BORROWS_LIGHT, PLANT_JITTER,
} from '../blocks';
import { type BlockReader, waterFlow } from '../world/fluid';
import { type ChunkTints, defaultChunkTints, MASKED_TINT_TILES, TINT_KIND, TintKind } from '../biomeTint';
import { PAD_AREA, PAD_ORIGIN, PAD_VOLUME, PAD_W, type PaddedChunk } from '../world/padded';
import { modelQuads, shapeCover, type ModelNb } from '../models';
import { K as RS_K, RS_KIND } from '../world/redstone/defs';
import { jitter, plantJitterX, plantJitterZ } from '../shapes';

// --- VERTEX FORMAT -----------------------------------------------------------
// a (Uint16 x4): x, y, z as (local + 1) * 256;  w = tile
// b (Uint16 x4): u * 16, v * 16 (tile units);   z = sky | block << 4 | meta << 8;  w = 1 if the tint
//                only applies to alpha-masked texels (grass block sides); on liquids
//                w = flow code: 0 still, else 1 + scroll direction in uv space (256 steps)
// c (Uint8 x4, normalized): tint r, g, b (sRGB); a = ambient occlusion
export const POS_SCALE = 256;
export const UV_SCALE = 16;
/** The meta flags (normal id 0..2, wave, emissive, sway, fire) sit above the
 *  light byte in b[o+2]; the tile index has a[o+3] to itself. */
export const META_SHIFT = 8;
/** Bits of a tile index in the greedy mask key, the narrowest place a tile is packed. */
const TILE_BITS = 12;
/** Tile indices the packed formats can address. */
export const MAX_TILES = 1 << TILE_BITS;
if (TILE_COUNT > MAX_TILES) {
  throw new Error(`mesher overflow: ${TILE_COUNT} tiles > ${MAX_TILES} packable (mesher/greedy.ts)`);
}
/** a[o+3] of a vertex. */
export function packTile(tile: number): number {
  return tile;
}
/** b[o+2] of a vertex: `light` is sky | block << 4. */
export function packLightMeta(light: number, meta: number): number {
  return light | (meta << META_SHIFT);
}
/** meta bits 0..2: face normal id (+X,-X,+Y,-Y,+Z,-Z, 6 = cross/none, 7 = +Y drawn
 *  from both sides). Ids 0..5 are closed-volume faces: the cutout shader drops
 *  their back sides, so two blocks' faces on one cell boundary never z-fight. */
export const META_WAVE = 1 << 3;
export const META_EMISSIVE = 1 << 4;
export const META_SWAY = 1 << 5;
/** Fire animation: the cutout shader samples the animated fire sheets
 *  (render/fire.ts) instead of the atlas tile. A separate bit from META_WAVE,
 *  which also displaces geometry vertically (lava) and would sink the flame
 *  into the floor. */
export const META_FIRE = 1 << 6;
const NORMAL_NONE = 6;

/** Blocks at least this bright render self-lit (glowstone, lava, torches). */
const EMISSIVE_MIN_LIGHT = 10;

export interface PassMesh {
  a: Uint16Array;
  b: Uint16Array;
  c: Uint8Array;
  indices: Uint16Array | Uint32Array;
  vertexCount: number;
  indexCount: number;
  /** Vertical extent of the geometry (blocks) for tight culling bounds. */
  minY: number;
  maxY: number;
}

export interface MeshResult {
  opaque: PassMesh;
  cutout: PassMesh;
  translucent: PassMesh;
}

/** Growable packed vertex buffers for one render pass. */
class PassBuffers {
  a = new Uint16Array(4096 * 4);
  b = new Uint16Array(4096 * 4);
  c = new Uint8Array(4096 * 4);
  count = 0; // vertices
  minY = Infinity;
  maxY = -Infinity;

  reset(): void {
    this.count = 0;
    this.minY = Infinity;
    this.maxY = -Infinity;
  }

  vertex(
    x: number, y: number, z: number, u: number, v: number,
    tile: number, meta: number, light: number, r: number, g: number, b: number, ao: number,
    w: number = MASKED_TINT_TILES[tile]
  ): void {
    let o = this.count * 4;
    if (o === this.a.length) {
      const a = new Uint16Array(o * 2); a.set(this.a); this.a = a;
      const bb = new Uint16Array(o * 2); bb.set(this.b); this.b = bb;
      const c = new Uint8Array(o * 2); c.set(this.c); this.c = c;
    }
    this.a[o] = Math.round((x + 1) * POS_SCALE);
    this.a[o + 1] = Math.round((y + 1) * POS_SCALE);
    this.a[o + 2] = Math.round((z + 1) * POS_SCALE);
    this.a[o + 3] = packTile(tile);
    this.b[o] = Math.round(u * UV_SCALE);
    this.b[o + 1] = Math.round(v * UV_SCALE);
    this.b[o + 2] = packLightMeta(light, meta);
    this.b[o + 3] = w;
    this.c[o] = r;
    this.c[o + 1] = g;
    this.c[o + 2] = b;
    this.c[o + 3] = ao;
    if (y < this.minY) this.minY = y;
    if (y > this.maxY) this.maxY = y;
    this.count++;
  }

  finish(): PassMesh {
    const verts = this.count;
    const quads = verts >> 2;
    const indices = verts <= 65536 ? new Uint16Array(quads * 6) : new Uint32Array(quads * 6);
    for (let q = 0, i = 0, v = 0; q < quads; q++, i += 6, v += 4) {
      indices[i] = v; indices[i + 1] = v + 1; indices[i + 2] = v + 2;
      indices[i + 3] = v; indices[i + 4] = v + 2; indices[i + 5] = v + 3;
    }
    return {
      a: this.a.slice(0, verts * 4),
      b: this.b.slice(0, verts * 4),
      c: this.c.slice(0, verts * 4),
      indices,
      vertexCount: verts,
      indexCount: quads * 6,
      minY: verts > 0 ? this.minY : 0,
      maxY: verts > 0 ? this.maxY : 0,
    };
  }
}

/** Transferable buffers of a mesh result (for postMessage). */
export function meshTransferList(result: MeshResult): ArrayBuffer[] {
  const out: ArrayBuffer[] = [];
  for (const p of [result.opaque, result.cutout, result.translucent]) {
    out.push(p.a.buffer as ArrayBuffer, p.b.buffer as ArrayBuffer, p.c.buffer as ArrayBuffer, p.indices.buffer as ArrayBuffer);
  }
  return out;
}

const AO_BYTE = [107, 158, 212, 255]; // 0.42, 0.62, 0.83, 1.0
const FACE_INDEX = [[0, 1], [2, 3], [4, 5]]; // [+X,-X], [+Y,-Y], [+Z,-Z]
const DIMS = [CHUNK_SIZE, WORLD_HEIGHT, CHUNK_SIZE];
const STRIDE = [1, PAD_AREA, PAD_W]; // padded index stride per axis (x, y, z)

/** Corner offsets in (u,v): c0..c3 around the cell. */
const CORNER_U = [-1, 1, 1, -1];
const CORNER_V = [-1, -1, 1, 1];

// Mask layout: id (ID_BITS) | tile << ID_BITS (TILE_BITS) | dir | lowTop
const ID_BITS = 12;
const MASK_ID = (1 << ID_BITS) - 1;
const MASK_TILE = MAX_TILES - 1;
const MASK_DIR = 1 << (ID_BITS + TILE_BITS);
const MASK_LOW_TOP = MASK_DIR << 1;

// Scratch state, reused across jobs (a worker meshes one chunk at a time).
const passes = [new PassBuffers(), new PassBuffers(), new PassBuffers()];
const MAX_UV = Math.max(CHUNK_SIZE, WORLD_HEIGHT);
const maskA = new Int32Array(MAX_UV * MAX_UV);
const maskC01 = new Int32Array(MAX_UV * MAX_UV); // corner0<<16 | corner1
const maskC23 = new Int32Array(MAX_UV * MAX_UV); // corner2<<16 | corner3
const maskT = new Int32Array(MAX_UV * MAX_UV);   // face tint 0xRRGGBB
const cls = new Uint8Array(PAD_VOLUME);
const corners = new Int32Array(4); // packed: sky<<6 | block<<2 | ao
const plainsTints = defaultChunkTints();
const NO_TINT = 0xffffff;
const vx = [0, 0, 0, 0], vy = [0, 0, 0, 0], vz = [0, 0, 0, 0];
const vu = [0, 0, 0, 0], vv = [0, 0, 0, 0];
const pos = [0, 0, 0];

// T-junction removal (see emitMerged): every lattice point that is a corner of
// some face in this chunk, and the merged quads waiting for that set to be complete.
const LAT_W = CHUNK_SIZE + 1;
const LAT_AREA = LAT_W * LAT_W;
const LAT_STRIDE = [1, LAT_AREA, LAT_W]; // lattice index stride per axis (x, y, z)
const lattice = new Uint8Array(LAT_AREA * (WORLD_HEIGHT + 1));
const Q_INTS = 10; // d, s, i, j, w, h, mask, c01, c23, tint
let deferred = new Int32Array(Q_INTS * 1024);
let deferredN = 0;
const ringU: number[] = [];
const ringV: number[] = [];

let blocks: Uint16Array;
let light: Uint8Array;
let tints: ChunkTints;
let originX = 0;
let originZ = 0;

/**
 * Sample AO + smooth light for the 4 corners of a face.
 * `si` = padded index of the cell the face opens into (air side);
 * `su`, `sv` = padded strides along the greedy (u,v) axes.
 */
function sampleCorners(si: number, su: number, sv: number): void {
  const centerLight = light[si];
  const centerSky = centerLight >> 4;
  const centerBlk = centerLight & 0xf;

  for (let k = 0; k < 4; k++) {
    const i1 = si + CORNER_U[k] * su;
    const i2 = si + CORNER_V[k] * sv;
    const i3 = i1 + CORNER_V[k] * sv;
    const s1 = IS_OPAQUE[blocks[i1]];
    const s2 = IS_OPAQUE[blocks[i2]];
    const cc = IS_OPAQUE[blocks[i3]];
    const ao = s1 === 1 && s2 === 1 ? 0 : 3 - (s1 + s2 + cc);

    // smooth light: average light of non-opaque cells around the corner
    let skySum = centerSky, blkSum = centerBlk, cnt = 1;
    if (s1 === 0) { const l = light[i1]; skySum += l >> 4; blkSum += l & 0xf; cnt++; }
    if (s2 === 0) { const l = light[i2]; skySum += l >> 4; blkSum += l & 0xf; cnt++; }
    if (cc === 0) { const l = light[i3]; skySum += l >> 4; blkSum += l & 0xf; cnt++; }

    corners[k] = (Math.round(skySum / cnt) << 6) | (Math.round(blkSum / cnt) << 2) | ao;
  }
}

/** Tint (0xRRGGBB, sRGB) of block `id` showing `tile` in chunk-local column (x, z). */
function tintAt(id: number, tile: number, x: number, z: number): number {
  const kind = TINT_KIND[id];
  // liquids sample the grayscale liquid array, not the atlas tile — always tinted
  if (kind !== TintKind.Water && TINTABLE_TILES[tile] !== 1 && MASKED_TINT_TILES[tile] !== 1) return NO_TINT;
  if (kind === TintKind.Fixed) {
    const t = tintOf(id);
    return (Math.round(t[0] * 255) << 16) | (Math.round(t[1] * 255) << 8) | Math.round(t[2] * 255);
  }
  const map = kind === TintKind.Grass ? tints.grass : kind === TintKind.Foliage ? tints.foliage : tints.water;
  return map[x + z * CHUNK_SIZE];
}

/**
 * Mesh a padded chunk. `chunkTints` are the biome-blended colours per column
 * (see biomeTint.ts); omitted = uniform plains tint.
 */
export function meshChunk(padded: PaddedChunk, chunkTints?: ChunkTints): MeshResult {
  blocks = padded.blocks;
  light = padded.light;
  tints = chunkTints ?? plainsTints;
  originX = padded.cx * CHUNK_SIZE;
  originZ = padded.cz * CHUNK_SIZE;
  for (const p of passes) p.reset();

  // Highest non-air layer of the padded box — everything above is skipped.
  let topY = WORLD_HEIGHT - 1;
  scan: for (; topY >= 0; topY--) {
    const base = (topY + 1) * PAD_AREA;
    for (let i = 0; i < PAD_AREA; i++) if (blocks[base + i] !== AIR) break scan;
  }

  if (topY >= 0) {
    lattice.fill(0, 0, (topY + 2) * LAT_AREA);
    deferredN = 0;
    // per-voxel class for the mask fast path, up to the first all-air pad layer
    const clsEnd = Math.min(PAD_VOLUME, (topY + 3) * PAD_AREA);
    for (let i = 0; i < clsEnd; i++) {
      const id = blocks[i];
      cls[i] = id === AIR ? 1 : IS_OPAQUE[id] << 1;
      if (BORROWS_LIGHT[id] === 1) borrowLight(i);
    }
    for (let d = 0; d < 3; d++) {
      const u = (d + 1) % 3;
      const v = (d + 2) % 3;
      const sd = STRIDE[d];
      const su = STRIDE[u];
      const sv = STRIDE[v];
      const dimU = u === 1 ? topY + 1 : DIMS[u];
      const dimV = v === 1 ? topY + 1 : DIMS[v];
      const sliceCount = d === 1 ? topY + 1 : DIMS[d];

      for (let s = -1; s < sliceCount; s++) {
        // Build mask for this slice.
        let any = false;
        for (let j = 0; j < dimV; j++) {
          let ai = PAD_ORIGIN + s * sd + j * sv;
          let m = j * dimU;
          for (let i = 0; i < dimU; i++, ai += su, m++) {
            // Fast path: both opaque (underground) or both air — no face.
            // cls: 1 = air, 2 = opaque, 0 = other; equal non-zero class = no face.
            if ((cls[ai] & cls[ai + sd]) !== 0) {
              maskA[m] = 0;
              continue;
            }
            const aId = blocks[ai];
            // Face neighbor is along the slice normal (d).
            const bId = blocks[ai + sd];

            let selfId = 0;
            let dir = 0;
            if (aId !== AIR && RENDER_SHAPE[aId] === RenderShape.Cube && faceVisible(aId, bId)) {
              selfId = aId;
              dir = 1;
            } else if (bId !== AIR && RENDER_SHAPE[bId] === RenderShape.Cube && faceVisible(bId, aId)) {
              selfId = bId;
              dir = -1;
            }
            // Face ownership: only emit faces of this chunk's own blocks.
            // At s === -1 the a-cell is the neighbor's; at s === dim-1 the
            // b-cell is. (For d === 1 these are the world floor / sky pads.)
            if (selfId === 0 || (s === -1 && dir === 1) || (s === DIMS[d] - 1 && dir === -1)) {
              maskA[m] = 0;
              continue;
            }
            // Liquids never merge: vanilla tessellates every fluid cell with
            // per-corner heights (emitFluids). A merged quad would also
            // stretch the vertex wave across many blocks, so any neighbour
            // that subdivides the edge (shore, kelp, another chunk's split)
            // opens an animated T-junction crack.
            if (WATER_AMOUNT[selfId] > 0 || IS_LAVA[selfId] === 1) {
              maskA[m] = 0;
              continue;
            }
            const tile = BLOCKS[selfId].tiles[FACE_INDEX[d][dir === 1 ? 0 : 1]];
            let key = selfId | (tile << ID_BITS) | (dir === 1 ? MASK_DIR : 0);
            // Side faces of a liquid's surface layer get their top edge
            // lowered to meet the dropped top face (lava — water is meshed
            // per cell in emitFluids).
            if (d !== 1 && SURFACE_DROP[selfId] > 0) {
              const own = dir === 1 ? ai : ai + sd;
              const up = blocks[own + PAD_AREA];
              if (up !== selfId) key |= MASK_LOW_TOP;
            }
            maskA[m] = key;
            // The owning block's column picks the biome tint; faces only merge
            // while it stays the same, so every quad is flat-tinted.
            const own = dir === 1 ? s : s + 1;
            maskT[m] = d === 0 ? tintAt(selfId, tile, own, j)
              : d === 1 ? tintAt(selfId, tile, j, i)
              : tintAt(selfId, tile, i, own);

            // sample cell = the air side of the face (along the normal)
            sampleCorners(dir === 1 ? ai + sd : ai, su, sv);
            maskC01[m] = (corners[0] << 16) | corners[1];
            maskC23[m] = (corners[2] << 16) | corners[3];
            any = true;
          }
        }
        if (!any) continue;

        // Greedy merge + emit.
        for (let j = 0; j < dimV; j++) {
          for (let i = 0; i < dimU; ) {
            const m0 = j * dimU + i;
            const a = maskA[m0];
            if (a === 0) { i++; continue; }
            const c01 = maskC01[m0];
            const c23 = maskC23[m0];
            const tint = maskT[m0];

            // width along u
            let w = 1;
            while (
              i + w < dimU && maskA[m0 + w] === a && maskC01[m0 + w] === c01 &&
              maskC23[m0 + w] === c23 && maskT[m0 + w] === tint
            ) w++;

            // height along v
            let h = 1;
            outer: while (j + h < dimV) {
              const row = m0 + h * dimU;
              for (let k = 0; k < w; k++) {
                if (
                  maskA[row + k] !== a || maskC01[row + k] !== c01 ||
                  maskC23[row + k] !== c23 || maskT[row + k] !== tint
                ) break outer;
              }
              h++;
            }

            // every face marks its corners; merged quads wait until all are known
            const lb = (s + 1) * LAT_STRIDE[d] + i * LAT_STRIDE[u] + j * LAT_STRIDE[v];
            const lw = w * LAT_STRIDE[u], lh = h * LAT_STRIDE[v];
            lattice[lb] = lattice[lb + lw] = lattice[lb + lh] = lattice[lb + lw + lh] = 1;
            if (w === 1 && h === 1) {
              emitQuad(d, u, v, s, i, j, w, h, a, c01, c23, tint);
            } else {
              if (deferredN === deferred.length) {
                const grown = new Int32Array(deferredN * 2); grown.set(deferred); deferred = grown;
              }
              const q = deferredN;
              deferred[q] = d; deferred[q + 1] = s; deferred[q + 2] = i; deferred[q + 3] = j; deferred[q + 4] = w;
              deferred[q + 5] = h; deferred[q + 6] = a; deferred[q + 7] = c01; deferred[q + 8] = c23; deferred[q + 9] = tint;
              deferredN += Q_INTS;
            }

            // clear mask
            for (let dj = 0; dj < h; dj++) {
              const row = m0 + dj * dimU;
              for (let di = 0; di < w; di++) maskA[row + di] = 0;
            }
            i += w;
          }
        }
      }
    }

    if (deferredN > 0) {
      // model blocks (slabs, stairs, paths) end faces on the lattice too
      for (let y = 0; y <= topY; y++) {
        for (let z = 0; z < CHUNK_SIZE; z++) {
          let pi = PAD_ORIGIN + z * PAD_W + y * PAD_AREA;
          for (let x = 0; x < CHUNK_SIZE; x++, pi++) {
            const id = blocks[pi];
            if (id === AIR || RENDER_SHAPE[id] !== RenderShape.Model) continue;
            const lb = x + z * LAT_W + y * LAT_AREA;
            lattice[lb] = lattice[lb + 1] = lattice[lb + LAT_W] = lattice[lb + LAT_W + 1] = 1;
            lattice[lb + LAT_AREA] = lattice[lb + LAT_AREA + 1] = lattice[lb + LAT_AREA + LAT_W] = lattice[lb + LAT_AREA + LAT_W + 1] = 1;
          }
        }
      }
      for (let q = 0; q < deferredN; q += Q_INTS) {
        emitMerged(
          deferred[q], deferred[q + 1], deferred[q + 2], deferred[q + 3], deferred[q + 4],
          deferred[q + 5], deferred[q + 6], deferred[q + 7], deferred[q + 8], deferred[q + 9]
        );
      }
    }

    emitCrossPlants(topY);
    emitFluids(topY);
  }

  return {
    opaque: passes[0].finish(),
    cutout: passes[1].finish(),
    translucent: passes[2].finish(),
  };
}

/** Can a face of `selfId` be seen when the neighbor is `nbId`? */
function faceVisible(selfId: number, nbId: number): boolean {
  if (IS_OPAQUE[nbId] === 1) return false;
  // no internal faces inside a body of liquid or a glass pane wall
  if (selfId === nbId && (RENDER_PASS[selfId] === Pass.Translucent || selfId === GLASS_ID)) return false;
  // one lava body: no faces between source / flowing / falling cells
  if (IS_LAVA[selfId] === 1 && IS_LAVA[nbId] === 1) return false;
  // kelp / seagrass cells are part of the water body
  if (selfId === WATER_ID && IS_WATERY[nbId] === 1) return false;
  return true;
}

// Vertex emission order per (dir, flip) so the fixed index pattern yields the
// right winding (CCW seen from the face normal) and the AO-correct diagonal.
const ORDER_POS = [0, 1, 2, 3];
const ORDER_POS_FLIP = [1, 2, 3, 0];
const ORDER_NEG = [3, 2, 1, 0];
const ORDER_NEG_FLIP = [0, 3, 2, 1];

function emitQuad(
  d: number, u: number, v: number, s: number, i: number, j: number, w: number, h: number,
  mask: number, c01: number, c23: number, tint: number
): void {
  const selfId = mask & MASK_ID;
  const tile = (mask >> ID_BITS) & MASK_TILE;
  const dir = (mask & MASK_DIR) !== 0 ? 1 : -1;
  const lowTop = (mask & MASK_LOW_TOP) !== 0;
  const c = [(c01 >> 16) & 0xffff, c01 & 0xffff, (c23 >> 16) & 0xffff, c23 & 0xffff];

  const pass = RENDER_PASS[selfId];
  const pb = passes[pass === Pass.Opaque ? 0 : pass === Pass.Cutout ? 1 : 2];
  const drop = SURFACE_DROP[selfId];
  const face = FACE_INDEX[d][dir === 1 ? 0 : 1];
  const isTop = d === 1 && dir === 1;

  let meta = face;
  if (LIGHT_EMISSION[selfId] >= EMISSIVE_MIN_LIGHT) meta |= META_EMISSIVE;
  // leaves: cutout cubes that filter light
  if (pass === Pass.Cutout && LIGHT_FILTER[selfId] > 0) meta |= META_SWAY;

  for (let k = 0; k < 4; k++) {
    const ou = k === 1 || k === 2 ? w : 0;
    const ov = k >= 2 ? h : 0;
    // The face plane is always the boundary between the pair (s, s+1).
    pos[d] = s + 1;
    pos[u] = i + ou;
    pos[v] = j + ov;
    let y = pos[1];
    if (drop > 0) {
      if (isTop) y -= drop;
      else if (lowTop && ((d === 0 && ou !== 0) || (d === 2 && ov !== 0))) y -= drop;
    }
    vx[k] = pos[0]; vy[k] = y; vz[k] = pos[2];

    // uv: extent along the quad in tile units
    if (d === 0) {
      vu[k] = ov;          // z extent
      vv[k] = w - ou;      // y extent, flipped so image-top = block-top
    } else if (d === 1) {
      vu[k] = ou;
      vv[k] = ov;
    } else {
      vu[k] = ou;
      vv[k] = h - ov;
    }
  }

  const flip = (c[0] & 3) + (c[2] & 3) > (c[1] & 3) + (c[3] & 3);
  const order = dir === 1 ? (flip ? ORDER_POS_FLIP : ORDER_POS) : (flip ? ORDER_NEG_FLIP : ORDER_NEG);

  for (let n = 0; n < 4; n++) {
    const k = order[n];
    const ck = c[k];
    pb.vertex(
      vx[k], vy[k], vz[k], vu[k], vv[k], tile, meta,
      ((ck >> 6) & 0xf) | (((ck >> 2) & 0xf) << 4),
      tint >> 16, (tint >> 8) & 0xff, tint & 0xff, AO_BYTE[ck & 3]
    );
  }
}

// --- merged quads without T-junctions ------------------------------------------
// Where a corner of one face lies part-way along an edge of a larger merged
// quad, the two edges are rasterised from different endpoints and leave
// single-pixel cracks that sparkle as the camera moves. So a merged quad takes
// every such lattice point on its perimeter as a vertex of its own: the edge is
// then made of the same segments, between bit-identical vertices, on both sides.
// The corner set is the chunk's own faces (lattice[]); the neighbour chunk's are
// not known here, so every lattice point on a chunk border plane counts — both
// chunks then agree without seeing each other's mesh. Faces stay exactly on the
// block grid (no overlap, no texture stretch); a quad nothing touches mid-edge is
// still two triangles.

const mSky = [0, 0, 0, 0], mBlk = [0, 0, 0, 0], mAo = [0, 0, 0, 0];
let mPb: PassBuffers, mD = 0, mS = 0, mI = 0, mJ = 0, mW = 1, mH = 1, mTile = 0, mMeta = 0, mTint = 0;

/** One vertex of the current merged quad at (ou, ov) of its (0..w, 0..h) extent. */
function mergedVertex(ou: number, ov: number): void {
  const u = (mD + 1) % 3, v = (mD + 2) % 3;
  pos[mD] = mS + 1;
  pos[u] = mI + ou;
  pos[v] = mJ + ov;
  const fu = ou / mW, fv = ov / mH;
  const w0 = (1 - fu) * (1 - fv), w1 = fu * (1 - fv), w2 = fu * fv, w3 = (1 - fu) * fv;
  const sky = Math.round(mSky[0] * w0 + mSky[1] * w1 + mSky[2] * w2 + mSky[3] * w3);
  const blk = Math.round(mBlk[0] * w0 + mBlk[1] * w1 + mBlk[2] * w2 + mBlk[3] * w3);
  const ao = Math.round(mAo[0] * w0 + mAo[1] * w1 + mAo[2] * w2 + mAo[3] * w3);
  // same uv convention as emitQuad
  const tu = mD === 0 ? ov : ou;
  const tv = mD === 0 ? mW - ou : mD === 1 ? ov : mH - ov;
  mPb.vertex(pos[0], pos[1], pos[2], tu, tv, mTile, mMeta, sky | (blk << 4), mTint >> 16, (mTint >> 8) & 0xff, mTint & 0xff, ao);
}

/** Two triangles (a, b, c), (a, c, e) of ring points, counter-clockwise in (u, v). */
function mergedPair(a: number, b: number, c: number, e: number, front: boolean): void {
  mergedVertex(ringU[a], ringV[a]);
  if (front) {
    mergedVertex(ringU[b], ringV[b]); mergedVertex(ringU[c], ringV[c]); mergedVertex(ringU[e], ringV[e]);
  } else {
    mergedVertex(ringU[e], ringV[e]); mergedVertex(ringU[c], ringV[c]); mergedVertex(ringU[b], ringV[b]);
  }
}

function emitMerged(
  d: number, s: number, i: number, j: number, w: number, h: number,
  mask: number, c01: number, c23: number, tint: number
): void {
  const u = (d + 1) % 3, v = (d + 2) % 3;
  const selfId = mask & MASK_ID;
  // lowered surfaces are off the lattice: nothing lines up with them anyway
  if (SURFACE_DROP[selfId] > 0) { emitQuad(d, u, v, s, i, j, w, h, mask, c01, c23, tint); return; }

  const su = LAT_STRIDE[u], sv = LAT_STRIDE[v];
  const base = (s + 1) * LAT_STRIDE[d] + i * su + j * sv;
  // on a chunk border plane (x or z = 0 / CHUNK_SIZE) every lattice point counts
  const inPlane = d !== 1 && (s + 1 === 0 || s + 1 === CHUNK_SIZE);
  const allU0 = inPlane || (v !== 1 && j === 0);
  const allU1 = inPlane || (v !== 1 && j + h === CHUNK_SIZE);
  const allV0 = inPlane || (u !== 1 && i === 0);
  const allV1 = inPlane || (u !== 1 && i + w === CHUNK_SIZE);

  // perimeter, counter-clockwise in (u, v): (0,0) -> (w,0) -> (w,h) -> (0,h)
  let n = 0;
  ringU[n] = 0; ringV[n++] = 0;
  for (let k = 1; k < w; k++) if (allU0 || lattice[base + k * su] === 1) { ringU[n] = k; ringV[n++] = 0; }
  const cornerB = n;
  ringU[n] = w; ringV[n++] = 0;
  for (let k = 1; k < h; k++) if (allV1 || lattice[base + w * su + k * sv] === 1) { ringU[n] = w; ringV[n++] = k; }
  const cornerC = n;
  ringU[n] = w; ringV[n++] = h;
  for (let k = w - 1; k > 0; k--) if (allU1 || lattice[base + k * su + h * sv] === 1) { ringU[n] = k; ringV[n++] = h; }
  ringU[n] = 0; ringV[n++] = h;
  for (let k = h - 1; k > 0; k--) if (allV0 || lattice[base + k * sv] === 1) { ringU[n] = 0; ringV[n++] = k; }
  if (n === 4) { emitQuad(d, u, v, s, i, j, w, h, mask, c01, c23, tint); return; }

  const pass = RENDER_PASS[selfId];
  mPb = passes[pass === Pass.Opaque ? 0 : pass === Pass.Cutout ? 1 : 2];
  const front = (mask & MASK_DIR) !== 0;
  mD = d; mS = s; mI = i; mJ = j; mW = w; mH = h; mTint = tint;
  mTile = (mask >> ID_BITS) & MASK_TILE;
  mMeta = FACE_INDEX[d][front ? 0 : 1];
  if (LIGHT_EMISSION[selfId] >= EMISSIVE_MIN_LIGHT) mMeta |= META_EMISSIVE;
  if (pass === Pass.Cutout && LIGHT_FILTER[selfId] > 0) mMeta |= META_SWAY;
  for (let k = 0; k < 4; k++) {
    const ck = k === 0 ? (c01 >> 16) & 0xffff : k === 1 ? c01 & 0xffff : k === 2 ? (c23 >> 16) & 0xffff : c23 & 0xffff;
    mSky[k] = (ck >> 6) & 0xf; mBlk[k] = (ck >> 2) & 0xf; mAo[k] = AO_BYTE[ck & 3];
  }

  if (w > 1 && h > 1) {
    // fan around the centre (a merged block of faces is flat-lit, so the centre adds no shading error)
    ringU[n] = w / 2; ringV[n] = h / 2;
    for (let k = 0; k < n; k += 2) {
      const b = k + 1 < n ? k + 1 : 0;
      mergedPair(n, k, b, k + 1 < n ? (k + 2) % n : b, front);
    }
    return;
  }

  // A one-wide strip: zigzag between its two long edges, so light that differs
  // from one edge to the other is interpolated exactly as on the plain quad.
  // Rail A runs forward through the ring, rail B backward; both ascend along the strip.
  const alongU = h === 1;
  const a0 = alongU ? 0 : cornerB, a1 = alongU ? cornerB : cornerC;
  const t = alongU ? ringU : ringV;
  let ia = a0;                // current point on A (ring index)
  let ib = (a0 + n - 1) % n;  // current point on B
  let stepsA = a1 - a0, stepsB = n - (a1 - a0 + 1) - 1;
  // each step adds one triangle on the diagonal (ia, ib); two consecutive ones share it and pack into one quad
  let q0 = -1, q1 = 0, q2 = 0;
  while (stepsA > 0 || stepsB > 0) {
    const nb = (ib + n - 1) % n;
    const advA = stepsB === 0 || (stepsA > 0 && t[ia + 1] <= t[nb]);
    const added = advA ? ia + 1 : nb;
    if (q0 < 0) {
      // first of a pair: (ia, ia+1, ib) rotated to start on ib, or (ia, nb, ib) rotated to start on nb
      if (advA) { q0 = ib; q1 = ia; q2 = ia + 1; } else { q0 = nb; q1 = ib; q2 = ia; }
    } else {
      mergedPair(q0, q1, q2, added, front);
      q0 = -1;
    }
    if (advA) { ia++; stepsA--; } else { ib = nb; stepsB--; }
  }
  if (q0 >= 0) mergedPair(q0, q1, q2, q2, front);
}

// --- fluids -----------------------------------------------------------------
// Vanilla liquid model: every cell is tessellated on its own (never merged)
// with per-corner heights — the weighted average of the water heights of the
// four cells around each corner — so flowing water slopes down from its
// source and shared corners agree by construction (no seams). Still water
// is flat at 8/9; the texture scrolls along the flow.

/**
 * Surface height of a cell as its neighbours' corners see it: water level, 0 = open, -1 = solid.
 */
let curLava = false; // fluid being meshed: lava cells only blend with lava, water with water
function fluidHeight(pi: number): number {
  const id = blocks[pi];
  const amt = curLava ? LAVA_AMOUNT : WATER_AMOUNT;
  const amount = amt[id];
  if (amount > 0) return amt[blocks[pi + PAD_AREA]] > 0 ? 1 : amount / 9;
  return IS_SOLID[id] === 1 ? -1 : 0;
}

let hSum = 0;
let hWeight = 0;
/** Nearly full water dominates a corner; solid neighbours (-1) don't count. */
function addHeight(h: number): void {
  if (h >= 0.8) {
    hSum += h * 10;
    hWeight += 10;
  } else if (h >= 0) {
    hSum += h;
    hWeight += 1;
  }
}

/** Height of the corner shared by a cell, two side neighbours and the diagonal one. */
function cornerHeight(own: number, h1: number, h2: number, diag: number): number {
  if (h1 >= 1 || h2 >= 1) return 1;
  hSum = 0;
  hWeight = 0;
  if (h1 > 0 || h2 > 0) {
    const hd = fluidHeight(diag);
    if (hd >= 1) return 1;
    addHeight(hd);
  }
  addHeight(own);
  addHeight(h1);
  addHeight(h2);
  return hSum / hWeight;
}

/** Flow code of a uv-space scroll direction (see VERTEX FORMAT). */
function flowCode(du: number, dv: number): number {
  return 1 + ((Math.round((Math.atan2(dv, du) / (Math.PI * 2)) * 256) + 256) & 255);
}
/** Side faces run downwards (v grows towards the block bottom). */
const FLOW_DOWN = flowCode(0, 1);

const cornerH = [0, 0, 0, 0]; // top corner heights, index = x offset + 2 * z offset
const flowVec = [0, 0, 0];
const off = [0, 0, 0];
const waveK = [0, 0, 0, 0];
const readLocal: BlockReader = (x, y, z) => blocks[PAD_ORIGIN + x + z * PAD_W + y * PAD_AREA];

function emitFluids(topY: number): void {
  const pb = passes[2];
  for (let y = 0; y <= topY; y++) {
    for (let z = 0; z < CHUNK_SIZE; z++) {
      let pi = PAD_ORIGIN + y * PAD_AREA + z * PAD_W;
      for (let x = 0; x < CHUNK_SIZE; x++, pi++) {
        const id = blocks[pi];
        const lava = IS_LAVA[id] === 1;
        if ((!lava && WATER_AMOUNT[id] === 0) || RENDER_SHAPE[id] !== RenderShape.Cube) continue;
        curLava = lava;
        const fid = lava ? LAVA_ID : WATER_ID;
        const tile = BLOCKS[fid].tiles[0];

        const own = fluidHeight(pi);
        if (own >= 1) {
          cornerH[0] = cornerH[1] = cornerH[2] = cornerH[3] = 1;
        } else {
          const hW = fluidHeight(pi - 1);
          const hE = fluidHeight(pi + 1);
          const hN = fluidHeight(pi - PAD_W);
          const hS = fluidHeight(pi + PAD_W);
          cornerH[0] = cornerHeight(own, hW, hN, pi - 1 - PAD_W);
          cornerH[1] = cornerHeight(own, hE, hN, pi + 1 - PAD_W);
          cornerH[2] = cornerHeight(own, hW, hS, pi - 1 + PAD_W);
          cornerH[3] = cornerHeight(own, hE, hS, pi + 1 + PAD_W);
        }

        const tint = tintAt(fid, tile, x, z);
        const above = blocks[pi + PAD_AREA];
        if (faceVisible(fid, above)) {
          if (!lava) waterFlow(readLocal, x, y, z, flowVec); else { flowVec[0] = 0; flowVec[2] = 0; }
          // top-face uv is (z, x)
          const still = flowVec[0] === 0 && flowVec[2] === 0;
          emitFluidFace(pb, 1, 1, x, y, z, pi + PAD_AREA, tile, tint, lava,
            still ? 0 : flowCode(flowVec[2], flowVec[0]));
        }
        if (faceVisible(fid, blocks[pi - PAD_AREA])) emitFluidFace(pb, 1, -1, x, y, z, pi - PAD_AREA, tile, tint, lava, 0);
        for (let d = 0; d < 3; d += 2) {
          for (let dir = -1; dir <= 1; dir += 2) {
            const ni = pi + dir * STRIDE[d];
            if (faceVisible(fid, blocks[ni])) emitFluidFace(pb, d, dir, x, y, z, ni, tile, tint, lava, lava ? 0 : FLOW_DOWN);
          }
        }
      }
    }
  }
}

/** One face of a sloped water cell; `si` is the cell its light is sampled from. */
function emitFluidFace(
  pb: PassBuffers, d: number, dir: number, x: number, y: number, z: number, si: number,
  tile: number, tint: number, lava: boolean, flow: number
): void {
  const u = (d + 1) % 3;
  const v = (d + 2) % 3;
  sampleCorners(si, STRIDE[u], STRIDE[v]);
  for (let k = 0; k < 4; k++) {
    const ou = k === 1 || k === 2 ? 1 : 0;
    const ov = k >= 2 ? 1 : 0;
    off[d] = dir === 1 ? 1 : 0;
    off[u] = ou;
    off[v] = ov;
    const h = off[1] === 1 ? cornerH[off[0] + off[2] * 2] : 0;
    vx[k] = x + off[0];
    vy[k] = y + h;
    vz[k] = z + off[2];
    if (d === 1) {
      vu[k] = ou;
      vv[k] = ov;
    } else {
      vu[k] = d === 0 ? off[2] : off[0];
      vv[k] = 1 - h;
    }
    // surface corners level with still water bob with it (welds to the greedy quads)
    waveK[k] = lava ? META_EMISSIVE : off[1] === 1 && h >= 0.8 && h < 1 ? META_WAVE : 0;
  }

  const face = FACE_INDEX[d][dir === 1 ? 0 : 1];
  const flip = (corners[0] & 3) + (corners[2] & 3) > (corners[1] & 3) + (corners[3] & 3);
  const order = dir === 1 ? (flip ? ORDER_POS_FLIP : ORDER_POS) : (flip ? ORDER_NEG_FLIP : ORDER_NEG);
  for (let n = 0; n < 4; n++) {
    const k = order[n];
    const ck = corners[k];
    pb.vertex(
      vx[k], vy[k], vz[k], vu[k], vv[k], tile, face | waveK[k],
      ((ck >> 6) & 0xf) | (((ck >> 2) & 0xf) << 4),
      tint >> 16, (tint >> 8) & 0xff, tint & 0xff, AO_BYTE[ck & 3], flow
    );
  }
}

/** Neighbour offsets by face (+X, -X, +Y, -Y, +Z, -Z) in the padded arrays. */
const NB_OFF = [1, -1, PAD_AREA, -PAD_AREA, PAD_W, -PAD_W];

/**
 * Slabs and stairs filter all light, so their own cell stores none. Give the cell the brightest light
 * of its see-through neighbours, so faces of full blocks beside it (which sample this cell) are not
 * black and the shape itself is lit like the air it sits in. Other slab / stair cells are skipped,
 * so a roof of slabs cannot pass light along itself.
 */
function borrowLight(i: number): void {
  let sky = 0, blk = 0;
  for (let d = 0; d < 6; d++) {
    const j = i + NB_OFF[d];
    if (j < 0 || j >= PAD_VOLUME) continue;
    const nid = blocks[j];
    if (BORROWS_LIGHT[nid] === 1 || IS_OPAQUE[nid] === 1) continue;
    const l = light[j];
    if (l >> 4 > sky) sky = l >> 4;
    if ((l & 0xf) > blk) blk = l & 0xf;
  }
  light[i] = (sky << 4) | blk;
}

/** Which cell face (+X, -X, +Y, -Y, +Z, -Z) a quad lies flush on, or -1 when it is inside the cell. */
function boundaryDir(pos: ArrayLike<number>): number {
  for (let a = 0; a < 3; a++) {
    const c = pos[a];
    if ((c === 0 || c === 1) && pos[3 + a] === c && pos[6 + a] === c && pos[9 + a] === c) return a * 2 + (c === 1 ? 0 : 1);
  }
  return -1;
}

/** Is the quad's footprint on its face plane inside one of the rectangles (1/16, other axes ascending)? */
function quadCovered(pos: ArrayLike<number>, dir: number, rects: ReadonlyArray<readonly number[]>): boolean {
  const a = dir >> 1, u = (a + 1) % 3, v = (a + 2) % 3;
  let u0 = 99, u1 = -99, v0 = 99, v1 = -99;
  for (let k = 0; k < 4; k++) {
    const pu = pos[k * 3 + u] * 16, pv = pos[k * 3 + v] * 16;
    if (pu < u0) u0 = pu; if (pu > u1) u1 = pu;
    if (pv < v0) v0 = pv; if (pv > v1) v1 = pv;
  }
  for (const r of rects) if (u0 >= r[0] - 1e-3 && v0 >= r[1] - 1e-3 && u1 <= r[2] + 1e-3 && v1 <= r[3] + 1e-3) return true;
  return false;
}

/** X-shaped plants (two crossed quads, no AO), torch posts and flat ground cover. */
function emitCrossPlants(topY: number): void {
  const pb = passes[1]; // cutout pass
  for (let y = 0; y <= topY; y++) {
    for (let z = 0; z < CHUNK_SIZE; z++) {
      let pi = PAD_ORIGIN + y * PAD_AREA + z * PAD_W;
      for (let x = 0; x < CHUNK_SIZE; x++, pi++) {
        const id = blocks[pi];
        if (id === AIR || RENDER_SHAPE[id] === RenderShape.Cube) continue;
        if (RENDER_SHAPE[id] === RenderShape.Flat) {
          emitFlat(pb, pi, x, y, z, id);
          continue;
        }
        if (RENDER_SHAPE[id] === RenderShape.Rail) {
          emitRail(pb, pi, x, y, z, id);
          continue;
        }
        // light[] packs sky << 4 | block; the vertex format wants sky | block << 4
        const raw = light[pi];
        const l = (raw >> 4) | ((raw & 0xf) << 4);
        // vanilla kelp: only the tip uses `kelp`; every cell with kelp above it
        // is `kelp_plant`, the stalk with the stem running through it
        const tile = id === KELP_ID && blocks[pi + PAD_AREA] === KELP_ID ? T.KELP_PLANT : BLOCKS[id].tiles[0];

        if (RENDER_SHAPE[id] === RenderShape.Fire) {
          emitFire(pb, x, y, z, tile, l);
          continue;
        }

        if (RENDER_SHAPE[id] === RenderShape.Model) {
          // nether portal panes are see-through: blended in the translucent pass
          emitModel(id === NETHER_PORTAL_ID || id === NETHER_PORTAL_Z_ID ? passes[2] : pb, pi, x, y, z, id);
          continue;
        }

        if (IS_TORCH[id] === 1) {
          // The mount is in the block id (see blocks.ts): -1 stands on the
          // floor, otherwise the shaft leans off the wall it was placed on.
          const face = TORCH_FACING[id];
          if (face >= 0) {
            const dir = FACE_DIRS[face];
            emitWallTorch(pb, x, y, z, dir[0], dir[2], tile, l);
          } else {
            emitTorch(pb, x, y, z, tile, l);
          }
          continue;
        }

        const tint = tintAt(id, tile, x, z);
        const r = tint >> 16, g = (tint >> 8) & 0xff, b = tint & 0xff;
        // vanilla nudges plants off the block centre so fields don't look gridded
        const wx = originX + x, wz = originZ + z;
        const jit = PLANT_JITTER[id] === 1;
        const jx = jit ? plantJitterX(wx, wz) : 0;
        const jz = jit ? plantJitterZ(wx, wz) : 0;
        const meta = LIGHT_EMISSION[id] >= EMISSIVE_MIN_LIGHT ? NORMAL_NONE | META_EMISSIVE : NORMAL_NONE;
        const sway = IS_RIGID[id] === 1 ? 0 : META_SWAY;
        if (IS_CROP_MODEL[id] === 1) {
          // vanilla block/crop: planes at x = 4/16, 12/16 and z = 4/16, 12/16, y -1/16..15/16
          for (let q = 0; q < 4; q++) {
            const off = (q & 1) ? 0.75 : 0.25;
            const alongZ = q < 2;
            const x0 = alongZ ? x + off : x, x1 = alongZ ? x + off : x + 1;
            const z0 = alongZ ? z : z + off, z1 = alongZ ? z + 1 : z + off;
            const y0 = y - 1 / 16, y1 = y + 15 / 16;
            pb.vertex(x0, y0, z0, 0, 1, tile, meta, l, r, g, b, 255);
            pb.vertex(x1, y0, z1, 1, 1, tile, meta, l, r, g, b, 255);
            pb.vertex(x1, y1, z1, 1, 0, tile, meta, l, r, g, b, 255);
            pb.vertex(x0, y1, z0, 0, 0, tile, meta, l, r, g, b, 255);
          }
          continue;
        }
        for (let q = 0; q < 2; q++) {
          const x0 = x + jx + (q === 0 ? 0.08 : 0.92);
          const x1 = x + jx + (q === 0 ? 0.92 : 0.08);
          const z0 = z + jz + 0.08;
          const z1 = z + jz + 0.92;
          // only the top edge sways, the base stays rooted
          pb.vertex(x0, y, z0, 0, 1, tile, meta, l, r, g, b, 255);
          pb.vertex(x1, y, z1, 1, 1, tile, meta, l, r, g, b, 255);
          pb.vertex(x1, y + 1, z1, 1, 0, tile, meta | sway, l, r, g, b, 255);
          pb.vertex(x0, y + 1, z0, 0, 0, tile, meta | sway, l, r, g, b, 255);
        }
      }
    }
  }
}

// Flat quad corners (x, z), counter-clockwise seen from above; the same table
// gives the (u, v) of each corner, so rotating the index rotates the texture.
const FLAT_X = [0, 0, 1, 1];
const FLAT_Z = [0, 1, 1, 0];
const FACE_UP = 7; // +Y, two-sided: lit and shadowed like a top face, visible from below (lily pads)

/**
 * Ground cover (petals, lily pads, carpets): one horizontal quad low in the
 * cell, with the smooth light + AO the top face of the block below would get.
 * Like vanilla, the texture is turned 0/90/180/270 degrees per column.
 */
function emitFlat(pb: PassBuffers, pi: number, x: number, y: number, z: number, id: number): void {
  const tile = BLOCKS[id].tiles[2];
  const tint = tintAt(id, tile, x, z);
  const r = tint >> 16, g = (tint >> 8) & 0xff, b = tint & 0xff;
  // corner order of sampleCorners(u = z, v = x) matches FLAT_X / FLAT_Z
  sampleCorners(pi, PAD_W, 1);
  const rot = FLAT_ALIGNED[id] === 1 ? 0 : (jitter(originX + x, originZ + z, 0x3d) * 4) & 3;
  const fy = y + FLAT_Y[id];
  // same AO-correct diagonal as cube faces
  const flip = (corners[0] & 3) + (corners[2] & 3) > (corners[1] & 3) + (corners[3] & 3) ? 1 : 0;
  for (let n = 0; n < 4; n++) {
    const k = (n + flip) & 3;
    const uv = (k + rot) & 3;
    const ck = corners[k];
    pb.vertex(
      x + FLAT_X[k], fy, z + FLAT_Z[k], FLAT_X[uv], FLAT_Z[uv], tile, FACE_UP,
      ((ck >> 6) & 0xf) | (((ck >> 2) & 0xf) << 4), r, g, b, AO_BYTE[ck & 3]
    );
  }
}

/**
 * Rails (world/rails.ts shapes): the flat quad of ground cover, turned to run along the track, curved
 * tiles for the corners, and a tilted quad for the ascending slopes (the high edge one block up).
 */
const RAIL_ROT = [0, 1, 1, 1, 0, 0, 0, 1, 2, 3]; // texture quarter-turns per shape (corner art joins south + east)
function emitRail(pb: PassBuffers, pi: number, x: number, y: number, z: number, id: number): void {
  const shape = RAIL_SHAPE[id];
  const tile = BLOCKS[id].tiles[2];
  sampleCorners(pi, PAD_W, 1);
  const rot = RAIL_ROT[shape];
  // height of each corner (FLAT_X/FLAT_Z order): the slope's high side sits a block up
  const hi = (k: number): number => (shape === 2 ? FLAT_X[k] === 1 : shape === 3 ? FLAT_X[k] === 0 : shape === 4 ? FLAT_Z[k] === 0 : shape === 5 ? FLAT_Z[k] === 1 : false) ? 1 : 0;
  const flip = (corners[0] & 3) + (corners[2] & 3) > (corners[1] & 3) + (corners[3] & 3) ? 1 : 0;
  for (let n = 0; n < 4; n++) {
    const k = (n + flip) & 3;
    const uv = (k + rot) & 3;
    const ck = corners[k];
    pb.vertex(
      x + FLAT_X[k], y + 1 / 16 + hi(k), z + FLAT_Z[k], FLAT_X[uv], FLAT_Z[uv], tile, FACE_UP,
      ((ck >> 6) & 0xf) | (((ck >> 2) & 0xf) << 4), 255, 255, 255, AO_BYTE[ck & 3]
    );
  }
}

/**
 * Model-shape blocks (doors, chests, beds): the block's baked quads from
 * models.ts, offset into the cell. Light comes from the block's own cell, like
 * plants and torches; each quad carries its face id so it gets the same
 * directional shade as a cube face (oblique ones, e.g. a half-open lid, none).
 */
function emitModel(pb: PassBuffers, pi: number, x: number, y: number, z: number, id: number): void {
  const nb: ModelNb = {
    px: blocks[pi + 1], nx: blocks[pi - 1], pz: blocks[pi + PAD_W], nz: blocks[pi - PAD_W],
  };
  if (RS_KIND[id] === RS_K.DUST) nb.at = (dx, dy, dz) => blocks[pi + dx + dz * PAD_W + dy * PAD_AREA] ?? 0;
  const raw = light[pi];
  const l = (raw >> 4) | ((raw & 0xf) << 4);
  // nether portal panes: animated from the fire array (sheet 2), self-lit
  const flags = id === NETHER_PORTAL_ID || id === NETHER_PORTAL_Z_ID ? META_FIRE | META_EMISSIVE : 0;
  const shaped = BORROWS_LIGHT[id] === 1;
  for (const q of modelQuads(id, nb)) {
    const { pos, uv } = q;
    let ql = l;
    if (shaped) {
      // a slab / stair quad on the cell boundary: hidden by a covering neighbour, else lit by the
      // cell it opens into (an overhang's underside takes the dark room's light, not the sky's)
      const dir = boundaryDir(pos);
      if (dir >= 0) {
        const nbi = pi + NB_OFF[dir], nid = blocks[nbi];
        if (IS_OPAQUE[nid] === 1 && RENDER_SHAPE[nid] === RenderShape.Cube) continue;
        if (BORROWS_LIGHT[nid] === 1) {
          if (quadCovered(pos, dir, shapeCover(nid)![dir ^ 1])) continue;
        } else {
          const raw2 = light[nbi];
          ql = (raw2 >> 4) | ((raw2 & 0xf) << 4);
        }
      }
    }
    if (flags !== 0) {
      // drop pane faces that touch the next portal cell (no blended seams inside the sheet)
      const on = (axis: number, v: number): boolean => pos[axis] === v && pos[3 + axis] === v && pos[6 + axis] === v && pos[9 + axis] === v;
      const nbr = on(0, 1) ? blocks[pi + 1] : on(0, 0) ? blocks[pi - 1] : on(1, 1) ? blocks[pi + PAD_AREA]
        : on(1, 0) ? blocks[pi - PAD_AREA] : on(2, 1) ? blocks[pi + PAD_W] : on(2, 0) ? blocks[pi - PAD_W] : -1;
      if (nbr === NETHER_PORTAL_ID || nbr === NETHER_PORTAL_Z_ID) continue;
    }
    for (let k = 0; k < 4; k++) {
      pb.vertex(
        x + pos[k * 3], y + pos[k * 3 + 1], z + pos[k * 3 + 2], uv[k * 2], uv[k * 2 + 1],
        q.tile, q.face | flags, ql, q.tint ? q.tint[0] : 255, q.tint ? q.tint[1] : 255, q.tint ? q.tint[2] : 255, 255
      );
    }
  }
}

/**
 * Vanilla fire, transcribed from models/block/template_fire_floor.json:
 * four zero-thickness vertical planes through the cell centre, each 22.4/16
 * blocks tall and tilted 22.5deg outward about the cell centre, all using the
 * full texture. `shade: false` upstream, so the planes carry NORMAL_NONE and
 * no face lighting — the flame reads as self-lit regardless of surroundings.
 *
 * The tilt is what makes fire look volumetric: the planes splay out toward the
 * top, so from any angle you see two overlapping sheets rather than one flat
 * card. Rotating about the cell centre also keeps every corner inside the
 * block bounds, which matters because the mesher buckets cutout geometry by
 * chunk cell.
 */
const FIRE_TILT = 22.5;
const FIRE_HEIGHT = 22.4 / 16;

/** Rotate a point about the cell centre by `deg` around axis 0 (X) or 1 (Z),
 *  in block units. Matches vanilla's `rotation: {origin, axis, angle}` on the
 *  model elements, which pivots all three axes, not just the horizontal ones. */
function fireTilt(
  px: number, py: number, pz: number, axis: 0 | 1, deg: number
): [number, number, number] {
  const r = (deg * Math.PI) / 180;
  const c = Math.cos(r), s = Math.sin(r);
  const dx = px - 0.5, dy = py - 0.5, dz = pz - 0.5;
  return axis === 0
    ? [px, 0.5 + dy * c - dz * s, 0.5 + dy * s + dz * c]
    : [0.5 + dx * c - dy * s, 0.5 + dx * s + dy * c, pz];
}

function emitFire(pb: PassBuffers, x: number, y: number, z: number, tile: number, l: number): void {
  // META_FIRE makes the cutout shader step through the adjacent fire_0/fire_1
  // atlas cells at ~10 fps, the way vanilla animates fire.
  const meta = NORMAL_NONE | META_EMISSIVE | META_FIRE;
  // (rotation axis, plane offset from the cell centre, tilt sign). Vanilla puts
  // the two +/-Z planes at z = 8.8 and 7.2 and the two +/-X planes at the same
  // offsets from centre — i.e. +-0.8/16 blocks.
  const planes: Array<[0 | 1, number, number]> = [
    [0, 0.8 / 16, -1], // facing +Z, tilted -22.5deg
    [0, -0.8 / 16, 1], // facing -Z, tilted +22.5deg
    [1, 0.8 / 16, -1], // facing +X, tilted -22.5deg
    [1, -0.8 / 16, 1], // facing -X, tilted +22.5deg
  ];
  for (const [axis, off, sign] of planes) {
    // base pair then top pair, running the full cell width on the plane's axis
    const corners: Array<[number, number]> = [[0, 0], [1, 0], [1, 1], [0, 1]];
    const pts = corners.map(([u, v]): [number, number, number] =>
      // axis 0 -> plane lies along X at z = off; axis 1 -> along Z at x = off
      fireTilt(
        axis === 0 ? u : 0.5 + off, v * FIRE_HEIGHT, axis === 0 ? 0.5 + off : u,
        axis, sign * FIRE_TILT
      )
    );
    // full-texture UVs, v flipped so the flame points up
    const uvs: Array<[number, number]> = [[0, 1], [1, 1], [1, 0], [0, 0]];
    // wind each plane so its front faces outward from the tilt
    for (let k = 0; k < 4; k++) {
      const p = pts[k];
      pb.vertex(
        x + p[0], y + p[1], z + p[2], uvs[k][0], uvs[k][1], tile, meta, l, 255, 255, 255, 255
      );
    }
  }
}
function emitTorch(pb: PassBuffers, x: number, y: number, z: number, tile: number, l: number): void {
  const meta = NORMAL_NONE | META_EMISSIVE;
  const x0 = x + 7 / 16, x1 = x + 9 / 16;
  const z0 = z + 7 / 16, z1 = z + 9 / 16;
  const y1 = y + 10 / 16;
  const u0 = 7 / 16, u1 = 9 / 16, vt = 6 / 16, vb = 1;
  const quad = (
    ax: number, az: number, bx: number, bz: number
  ): void => {
    pb.vertex(ax, y, az, u0, vb, tile, meta, l, 255, 255, 255, 255);
    pb.vertex(bx, y, bz, u1, vb, tile, meta, l, 255, 255, 255, 255);
    pb.vertex(bx, y1, bz, u1, vt, tile, meta, l, 255, 255, 255, 255);
    pb.vertex(ax, y1, az, u0, vt, tile, meta, l, 255, 255, 255, 255);
  };
  quad(x0, z1, x1, z1); // +Z
  quad(x1, z0, x0, z0); // -Z
  quad(x1, z1, x1, z0); // +X
  quad(x0, z0, x0, z1); // -X
  // top: the flame pixels
  pb.vertex(x0, y1, z1, u0, 8 / 16, tile, meta, l, 255, 255, 255, 255);
  pb.vertex(x1, y1, z1, u1, 8 / 16, tile, meta, l, 255, 255, 255, 255);
  pb.vertex(x1, y1, z0, u1, vt, tile, meta, l, 255, 255, 255, 255);
  pb.vertex(x0, y1, z0, u0, vt, tile, meta, l, 255, 255, 255, 255);
}

/** 22.5°: vanilla's wall-torch lean (template_torch_wall). */
const TORCH_LEAN = Math.PI / 8;

/**
 * Emit one planar quad, wound counter-clockwise seen from `out`. Only the sign
 * of the dot product matters, so an approximate (axis-aligned) direction is
 * enough — this is how the leaning wall torch keeps correct facing.
 */
function emitOrientedQuad(
  pb: PassBuffers, p: number[][], uv: number[][], out: readonly number[],
  tile: number, meta: number, l: number
): void {
  const ax = p[1][0] - p[0][0], ay = p[1][1] - p[0][1], az = p[1][2] - p[0][2];
  const bx = p[2][0] - p[1][0], by = p[2][1] - p[1][1], bz = p[2][2] - p[1][2];
  const nx = ay * bz - az * by, ny = az * bx - ax * bz, nz = ax * by - ay * bx;
  const order = nx * out[0] + ny * out[1] + nz * out[2] < 0 ? [0, 3, 2, 1] : [0, 1, 2, 3];
  for (let i = 0; i < 4; i++) {
    const k = order[i];
    pb.vertex(p[k][0], p[k][1], p[k][2], uv[k][0], uv[k][1], tile, meta, l, 255, 255, 255, 255);
  }
}

/**
 * Wall torch: vanilla's template_torch_wall — a 2x2x10px shaft leaning 22.5°
 * off the wall, foot on the wall plane 3.5px up, half its thickness buried in
 * the wall. Without the lean the post floats beside the block it hangs on.
 * `dx, dz` point from the torch cell to the wall it is mounted on.
 */
function emitWallTorch(
  pb: PassBuffers, x: number, y: number, z: number, dx: number, dz: number, tile: number, l: number
): void {
  const meta = NORMAL_NONE | META_EMISSIVE;
  const C = Math.cos(TORCH_LEAN), S = Math.sin(TORCH_LEAN);
  const U0 = -1 / 16, U1 = 1 / 16; // shaft thickness, straddling the wall plane
  const W0 = 7 / 16, W1 = 9 / 16; // shaft width, centred in the cell
  const BASE = 3.5 / 16, LEN = 10 / 16;
  const alongX = dx !== 0;
  const lean = alongX ? -dx : -dz; // away from the wall, into the cell
  const wall = lean > 0 ? 0 : 1; // the cell face the wall sits behind

  // Point at (u = across the shaft, w = its width, v = up the shaft): the shaft
  // is a rectangle in the (lean, up) plane, rotated by the lean angle.
  const at = (u: number, w: number, v: number): number[] => {
    const off = u * C + v * S; // distance out from the wall plane
    const up = BASE + v * C - u * S;
    const a = wall + lean * off;
    return alongX ? [x + a, y + up, z + w] : [x + w, y + up, z + a];
  };
  const emit = (
    params: number[][], out: readonly number[],
    across: (u: number, w: number) => number, up: (v: number, w: number) => number
  ): void => {
    const p = params.map(([u, w, v]) => at(u, w, v));
    const uv = params.map(([u, w, v]) => [across(u, w), up(v, w)]);
    emitOrientedQuad(pb, p, uv, out, tile, meta, l);
  };

  // the 2px dimensions sample the torch texture's 7..9/16 columns, the 10px
  // length its 6/16..1 rows (v flipped, matching the standing torch)
  const acrossU = (u: number): number => (u < 0 ? 7 / 16 : 9 / 16);
  const acrossW = (_u: number, w: number): number => (w === W0 ? 7 / 16 : 9 / 16);
  const sideV = (v: number): number => (v === 0 ? 1 : 6 / 16);
  const flameV = (_v: number, w: number): number => (w === W0 ? 6 / 16 : 8 / 16);
  const emberV = (_v: number, w: number): number => (w === W0 ? 13 / 16 : 15 / 16);

  const negLean: readonly number[] = alongX ? [-lean, 0, 0] : [0, 0, -lean];
  const leanOut: readonly number[] = alongX ? [lean, 0, 0] : [0, 0, lean];
  const negWidth: readonly number[] = alongX ? [0, 0, -1] : [-1, 0, 0];
  const widthOut: readonly number[] = alongX ? [0, 0, 1] : [1, 0, 0];

  emit([[U0, W0, 0], [U1, W0, 0], [U1, W0, LEN], [U0, W0, LEN]], negWidth, acrossU, sideV);
  emit([[U0, W1, 0], [U1, W1, 0], [U1, W1, LEN], [U0, W1, LEN]], widthOut, acrossU, sideV);
  emit([[U1, W0, 0], [U1, W1, 0], [U1, W1, LEN], [U1, W0, LEN]], leanOut, acrossW, sideV);
  emit([[U0, W0, 0], [U0, W1, 0], [U0, W1, LEN], [U0, W0, LEN]], negLean, acrossW, sideV);
  emit([[U0, W0, LEN], [U1, W0, LEN], [U1, W1, LEN], [U0, W1, LEN]], [0, 1, 0], acrossU, flameV);
  emit([[U0, W0, 0], [U1, W0, 0], [U1, W1, 0], [U0, W1, 0]], [0, -1, 0], acrossU, emberV);
}
